import { API_ROUTES } from "lib/constants/api-constants";
import useApi from "./useApi";
import { useState } from "react";

interface AnalyticsState {
  error: string | null;
  loading: boolean;
  clinicsBookingGraphLoading: boolean;
  clinicRevenueGraphLoading: boolean;
  clinicSubscriptionsGraphLoading: boolean;
  clinicOccupancyGraphLoading: boolean;
  clinicRoomRevenueGraphLoading: boolean;
  clinicPractitionersRevenueGraphLoading: boolean;
  clinicPractitionerRetentionGraphLoading: boolean;
  getClinics: (onSuccess?: (clinics: Clinic[]) => void) => Promise<void>;
  getTherapies: (onSuccess?: (therapies: Therapy[]) => void) => Promise<void>;
  getPractitioners: (
    onSuccess?: (practitioners: PractitionerDetails[]) => void
  ) => Promise<void>;
  getClinicDetails: (
    clinicId: string,
    onSuccess?: (clinic: ClinicAnalytics) => void
  ) => Promise<void>;
  getTherapyDetails: (
    therapyId: string,
    onSuccess?: (therapy: TherapyAnalyticsDetails) => void
  ) => Promise<void>;
  getPractitionerDetails: (
    practitionerId: string,
    onSuccess?: (practitioner: PractitionerAnalyticsDetails) => void
  ) => Promise<void>;
  getClinicBookingsGraphData: (
    clinicId: string,
    onSuccess?: (data: BookingEntry[]) => void
  ) => void;
  getClinicRevenueGraphData: (
    clinicId: string,
    onSuccess?: (data: RevenueEntry[]) => void
  ) => void;
  getClinicSubscriptionsGraphData: (
    clinicId: string,
    onSuccess?: (data: SubscriptionEntry[]) => void
  ) => void;
  getClinicOccupancyGraphData: (
    clinicId: string,
    onSuccess?: (occupancyEntry: RoomOccupancyEntry[]) => void
  ) => void;
  getAllClinicOccupancyGraphData: (
    onSuccess?: (allClinicOccupancyEntry: AllClinicOccupancyEntry[]) => void
  ) => void;
  getClinicPractitionerRetentionGraphData: (
    clinicId: string,
    onSuccess?: (
      allClinicPractitionerRetentionEntry: PractitionerRetentionEntry[]
    ) => void
  ) => void;

  getClinicRoomRevenueGraphData: (
    clinicId: string,
    onSuccess?: (roomRevenueEntry: RoomRevenueEntry[]) => void
  ) => void;
  getClinicPractitionersRevenueGraphData: (
    clinicId: string,
    onSuccess?: (practitionerRevenueEntry: PractitionerRevenueEntry[]) => void
  ) => void;

  getPractitionerAppointments: (practitionerId: string, onSuccess?: (data: PractitionerAppointmentsAnalyticsData) => void) => Promise<PractitionerAppointmentsAnalyticsData>;

  getPractitionerClinics: (practitionerId: string, onSuccess?: (data: PractitionerClinicsAnalyticsData) => void) => Promise<PractitionerClinicsAnalyticsData>;

  getPractitionerSubscriptions: (practitionerId: string, onSuccess?: (data: PractitionerSubscriptionsAnalyticsData) => void) => Promise<PractitionerSubscriptionsAnalyticsData>;

  getPractitionerEarnings: (practitionerId: string, onSuccess?: (data: PractitionerEarningsAnalyticsData) => void) => Promise<PractitionerEarningsAnalyticsData>;
}

const useAnalytics = (): AnalyticsState => {
  const [error, setError] = useState<string | null>(null);
  const { getData: getClinicsRequest, loading: getClinicsRequestLoading } =
    useApi<Clinic[]>();
  const {
    getData: getClinicDetailsRequest,
    loading: getClinicDetailsRequestLoading,
  } = useApi<ClinicAnalytics>();

  const { getData: getTherapiesRequest, loading: getTherapiesRequestLoading } =
    useApi<Therapy[]>();

  const {
    getData: getTherapyDetailsRequest,
    loading: getTherapyDetailsRequestLoading,
  } = useApi<TherapyAnalyticsDetails>();

  const {
    getData: getPractitionersRequest,
    loading: getPractitionersRequestLoading,
  } = useApi<PractitionerDetails[]>();

  const {
    getData: getPractitionerDetailsRequest,
    loading: getPractitionerDetailsRequestLoading,
  } = useApi<PractitionerAnalyticsDetails>();

  const {
    getData: getClinicBookingsGraphRequest,
    loading: getClinicBookingsGraphRequestLoading,
  } = useApi<BookingEntry[]>();
  const {
    getData: getClinicRevenueGraphRequest,
    loading: getClinicRevenueGraphRequestLoading,
  } = useApi<RevenueEntry[]>();
  const {
    getData: getClinicSubscriptionsGraphRequest,
    loading: getClinicSubscriptionsGraphRequestLoading,
  } = useApi<SubscriptionEntry[]>();
  const {
    getData: getClinicOccupancyGraphRequest,
    loading: getClinicOccupancyGraphRequestLoading,
  } = useApi<RoomOccupancyEntry[]>();
  const {
    getData: getAllClinicOccupancyGraphRequest,
    loading: getAllClinicOccupancyGraphRequestLoading,
  } = useApi<AllClinicOccupancyEntry[]>();
  const {
    getData: getAllClinicPractitionerRetentionRateRequest,
    loading: getAllClinicPractitionerRetentionRateRequestLoading,
  } = useApi<PractitionerRetentionEntry[]>();
  const {
    getData: getClinicRoomRevenueGraphRequest,
    loading: getClinicRoomRevenueGraphRequestLoading,
  } = useApi<RoomRevenueEntry[]>();
  const {
    getData: getClinicPractitionersRevenueGraphRequest,
    loading: getClinicPractitionersRevenueGraphRequestLoading,
  } = useApi<PractitionerRevenueEntry[]>();

  const {
    getData: getAnalyticsRequest,
    loading: getAnalyticsRequestLoading,
  } = useApi<unknown>();

  const onFailure = (errorMessage: string) => {
    setError(errorMessage);
  };

  const getClinics = async (onSuccess?: (clinics: Clinic[]) => void) => {
    await getClinicsRequest(
      `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.clinics}${API_ROUTES.ANALYTICS.all}`,
      onSuccess,
      onFailure
    );
  };

  const getClinicDetails = async (
    clinicId: string,
    onSuccess?: (clinic: ClinicAnalytics) => void
  ) => {
    await getClinicDetailsRequest(
      `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.clinics}/${clinicId}`,
      onSuccess,
      onFailure
    );
  };

  const getTherapies = async (onSuccess?: (therapies: Therapy[]) => void) => {
    await getTherapiesRequest(
      `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.therapies}${API_ROUTES.ANALYTICS.all}`,
      onSuccess,
      onFailure
    );
  };

  const getTherapyDetails = async (
    therapyId: string,
    onSuccess?: (therapy: TherapyAnalyticsDetails) => void
  ) => {
    await getTherapyDetailsRequest(
      `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.therapies}/${therapyId}`,
      onSuccess,
      onFailure
    );
  };

  const getPractitioners = async (
    onSuccess?: (practitioners: PractitionerDetails[]) => void
  ) => {
    await getPractitionersRequest(
      `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.practitioners}${API_ROUTES.ANALYTICS.all}`,
      onSuccess,
      onFailure
    );
  };

  const getPractitionerDetails = async (
    practitionerId: string,
    onSuccess?: (practitioner: PractitionerAnalyticsDetails) => void
  ) => {
    await getPractitionerDetailsRequest(
      `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.practitioners}/${practitionerId}`,
      onSuccess,
      onFailure
    );
  };
  const getClinicBookingsGraphData = async (
    clinicId: string,
    onSuccess?: (bookingsEntry: BookingEntry[]) => void
  ) => {
    const endpoint = API_ROUTES.ANALYTICS.clinicsBookingsGraphData(clinicId);
    await getClinicBookingsGraphRequest(endpoint, onSuccess, onFailure);
  };
  const getClinicRevenueGraphData = async (
    clinicId: string,
    onSuccess?: (revenueEntry: RevenueEntry[]) => void
  ) => {
    const endpoint = API_ROUTES.ANALYTICS.clinicsRevenueGraphData(clinicId);
    await getClinicRevenueGraphRequest(endpoint, onSuccess, onFailure);
  };

  const getClinicSubscriptionsGraphData = async (
    clinicId: string,
    onSuccess?: (revenueEntry: SubscriptionEntry[]) => void
  ) => {
    const endpoint =
      API_ROUTES.ANALYTICS.clinicsSubscriptionsGraphData(clinicId);
    await getClinicSubscriptionsGraphRequest(endpoint, onSuccess, onFailure);
  };

  const getClinicOccupancyGraphData = async (
    clinicId: string,
    onSuccess?: (occupancyEntry: RoomOccupancyEntry[]) => void
  ) => {
    const endpoint = API_ROUTES.ANALYTICS.clinicsOccupancyGraphData(clinicId);
    await getClinicOccupancyGraphRequest(endpoint, onSuccess, onFailure);
  };

  const getAllClinicOccupancyGraphData = async (
    onSuccess?: (occupancyEntry: AllClinicOccupancyEntry[]) => void
  ) => {
    const endpoint = API_ROUTES.ANALYTICS.allClinicsOccupancy();
    await getAllClinicOccupancyGraphRequest(endpoint, onSuccess, onFailure);
  };
  const getClinicPractitionerRetentionGraphData = async (
    clinicId: string,
    onSuccess?: (retentionEntry: PractitionerRetentionEntry[]) => void
  ) => {
    const endpoint =
      API_ROUTES.ANALYTICS.clinicsPractitionerRetentionRate(clinicId);
    await getAllClinicPractitionerRetentionRateRequest(
      endpoint,
      onSuccess,
      onFailure
    );
  };

  const getClinicRoomRevenueGraphData = async (
    clinicId: string,
    onSuccess?: (revenueEntry: RoomRevenueEntry[]) => void
  ) => {
    const endpoint = API_ROUTES.ANALYTICS.clinicsRoomRevenueGraphData(clinicId);
    await getClinicRoomRevenueGraphRequest(endpoint, onSuccess, onFailure);
  };
  const getClinicPractitionersRevenueGraphData = async (
    clinicId: string,
    onSuccess?: (practitionerRevenueEntry: PractitionerRevenueEntry[]) => void
  ) => {
    const endpoint =
      API_ROUTES.ANALYTICS.clinicsPractitionersRevenueGraphData(clinicId);
    await getClinicPractitionersRevenueGraphRequest(
      endpoint,
      onSuccess,
      onFailure
    );
  };

  const getPractitionerAppointments = async (practitionerId: string, onSuccess?: (data: PractitionerAppointmentsAnalyticsData) => void): Promise<PractitionerAppointmentsAnalyticsData> => {
    const endpoint =
      API_ROUTES.ANALYTICS.PRACTITIONER.practitionerAppointments(practitionerId);
    return await getAnalyticsRequest(
      endpoint,
      (value) => onSuccess?.(value?.[0]),
      onFailure
    ) as PractitionerAppointmentsAnalyticsData;
  }

  const getPractitionerClinics = async (practitionerId: string, onSuccess?: (data: PractitionerClinicsAnalyticsData) => void): Promise<PractitionerClinicsAnalyticsData> => {
    const endpoint =
      API_ROUTES.ANALYTICS.PRACTITIONER.practitionerClinics(practitionerId);
    return await getAnalyticsRequest(
      endpoint,
      (value) => onSuccess?.(value?.[0]),
      onFailure
    ) as PractitionerClinicsAnalyticsData;
  }

  const getPractitionerSubscriptions = async (practitionerId: string, onSuccess?: (data: PractitionerSubscriptionsAnalyticsData) => void): Promise<PractitionerSubscriptionsAnalyticsData> => {
    const endpoint =
      API_ROUTES.ANALYTICS.PRACTITIONER.practitionerSubscriptions(practitionerId);
    return await getAnalyticsRequest(
      endpoint,
      (value) => onSuccess?.(value?.[0]),
      onFailure
    ) as PractitionerSubscriptionsAnalyticsData;
  }

  const getPractitionerEarnings = async (practitionerId: string, onSuccess?: (data: PractitionerEarningsAnalyticsData) => void): Promise<PractitionerEarningsAnalyticsData> => {
    const endpoint =
      API_ROUTES.ANALYTICS.PRACTITIONER.practitionerEarnings(practitionerId);
    return await getAnalyticsRequest(
      endpoint,
      (value) => onSuccess?.(value?.[0]),
      onFailure
    ) as PractitionerEarningsAnalyticsData;
  }

  return {
    error,
    loading:
      getClinicsRequestLoading ||
      getTherapiesRequestLoading ||
      getPractitionersRequestLoading ||
      getClinicDetailsRequestLoading ||
      getPractitionerDetailsRequestLoading ||
      getTherapyDetailsRequestLoading ||
      getAllClinicOccupancyGraphRequestLoading || getAnalyticsRequestLoading,
    
    clinicsBookingGraphLoading: getClinicBookingsGraphRequestLoading,
    clinicRevenueGraphLoading: getClinicRevenueGraphRequestLoading,
    clinicSubscriptionsGraphLoading: getClinicSubscriptionsGraphRequestLoading,
    clinicOccupancyGraphLoading: getClinicOccupancyGraphRequestLoading,
    clinicRoomRevenueGraphLoading: getClinicRoomRevenueGraphRequestLoading,
    getClinicPractitionersRevenueGraphData,
    clinicPractitionersRevenueGraphLoading:
      getClinicPractitionersRevenueGraphRequestLoading,
    clinicPractitionerRetentionGraphLoading:
      getAllClinicPractitionerRetentionRateRequestLoading,
    getClinics,
    getClinicDetails,
    getTherapies,
    getTherapyDetails,
    getPractitioners,
    getPractitionerDetails,
    getClinicBookingsGraphData,
    getClinicRevenueGraphData,
    getClinicSubscriptionsGraphData,
    getClinicOccupancyGraphData,

    getAllClinicOccupancyGraphData,
    getClinicPractitionerRetentionGraphData,
    getClinicRoomRevenueGraphData,

    getPractitionerAppointments,
    getPractitionerClinics,
    getPractitionerSubscriptions,
    getPractitionerEarnings
  };
};
export default useAnalytics;
